import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportService } from '../services/reportService';
import { userService } from '../services/userService';
import { adminService } from '../services/adminService';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import StatusText from '../components/common/StatusText';
import InlineToast from '../components/common/InlineToast';
import TableSkeleton from '../components/common/TableSkeleton';
import { exportReportToPdf } from '../utils/pdfExport';
import MultiSelectDropdown from '../components/common/MultiSelectDropdown';

const getWeekRange = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const start = new Date(d.setDate(diff));
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};

const WeeklyReportPage = () => {
  const { user, hasRole, loading: authLoading } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const current = getWeekRange(new Date());
    return { start: current.start, end: current.end };
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'info' });
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestMessage, setDigestMessage] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');

  const isManagerOrExec = hasRole('Manager') || hasRole('Executive') || hasRole('Admin');
  const isManager = hasRole('Manager');

  const loadEmployees = useCallback(async () => {
    try {
      let list = [];
      if (hasRole('Manager') && !hasRole('Executive') && !hasRole('Admin')) {
        const response = await userService.getDirectReports(user.id);
        list = response.data || [];
      } else {
        const response = await userService.getAllUsers();
        list = response.data || [];
      }
      const [clientsResponse, categoriesResponse] = await Promise.all([
        adminService.getClients(),
        adminService.getCategories()
      ]);
      setClients(clientsResponse?.data || []);
      setCategories(categoriesResponse?.data || []);
      setEmployees(list);
      setSelectedEmployeeIds([]);
    } catch (err) {
      setClients([]);
      setError('Failed to load employees.');
      setToast({ message: 'Failed to load employees.', type: 'error' });
    }
  }, [hasRole, user.id]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const empId = selectedEmployeeIds.length === 0 ? 'all' : selectedEmployeeIds.join(',');
      const response = await reportService.getWeeklyReport(
        empId,
        dateRange.start,
        dateRange.end,
        {
          clientId: filterClientId || undefined,
          priority: filterPriority || undefined,
          categoryId: filterCategoryId || undefined
        }
      );
      setReport(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load weekly report.');
      setToast({ message: err.response?.data?.message || 'Failed to load weekly report.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedEmployeeIds, dateRange.start, dateRange.end, filterClientId, filterPriority, filterCategoryId]);

  useEffect(() => {
    if (authLoading || !isManagerOrExec) return;
    loadEmployees();
  }, [authLoading, isManagerOrExec, loadEmployees]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDownloadCsv = async () => {
    const start = dateRange.start;
    const end = dateRange.end;
    const empId = selectedEmployeeIds.length === 0 ? 'all' : selectedEmployeeIds.join(',');
    const response = await reportService.exportWeeklyReport(empId, start, end, {
      clientId: filterClientId || undefined,
      priority: filterPriority || undefined,
      categoryId: filterCategoryId || undefined
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `weekly-report-${start}-to-${end}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = () => {
    if (!report) return;
    let empName = 'All Employees';
    if (selectedEmployeeIds.length === 1) {
      empName = employees.find(e => String(e.id) === selectedEmployeeIds[0])?.fullName || 'Employee';
    } else if (selectedEmployeeIds.length > 1) {
      empName = `${selectedEmployeeIds.length} Employees`;
    }
    exportReportToPdf(report, dateRange, empName);
  };

  const handleSendDigestNow = async () => {
    try {
      setSendingDigest(true);
      setDigestMessage('');
      const start = dateRange.start;
      const end = dateRange.end;
      const empId = selectedEmployeeIds.length === 0 ? 'all' : selectedEmployeeIds.join(',');
      await reportService.sendWeeklyDigestNow({
        employeeId: empId,
        weekStart: start,
        weekEnd: end,
        clientId: filterClientId || null,
        priority: filterPriority || null,
        categoryId: filterCategoryId || null
      });
      setDigestMessage('Digest sent successfully.');
      setToast({ message: 'Digest sent successfully.', type: 'success' });
    } catch (err) {
      setDigestMessage(err.response?.data?.message || 'Failed to send digest.');
      setToast({ message: err.response?.data?.message || 'Failed to send digest.', type: 'error' });
    } finally {
      setSendingDigest(false);
    }
  };

  if (authLoading) return <Loading />;
  if (!isManagerOrExec) return <Navigate to="/dashboard" replace />;

  const tasks = report?.tasks || [];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Employee Weekly Report</h1>
        <p className="text-gray-600">Review weekly work summary by customer and push weekly digests.</p>
      </div>

      {error && (
        <div className="p-4 rounded mb-4 bg-red-100 text-red-700">{error}</div>
      )}

      <div className="mb-4">
        <InlineToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      </div>

      <div className="sticky top-16 z-20 bg-gray-50 pb-3 space-y-3">
      <div className="bg-white p-4 rounded-lg shadow-md mb-6 grid grid-cols-1 md:grid-cols-6 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          <MultiSelectDropdown
            options={employees.map(e => ({ value: String(e.id), label: e.fullName || e.name || e.email }))}
            value={selectedEmployeeIds}
            onChange={setSelectedEmployeeIds}
            placeholder="All Employees"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={filterCategoryId}
            onChange={(e) => setFilterCategoryId(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Priorities</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      {/* <div className="bg-white p-4 rounded-lg shadow-md mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"> */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
          <select
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Clients</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            <option value="">All Priorities</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
          </select>
        </div> */}
      {/* </div> */}
      </div>

      {loading ? (
        <div className="mt-4">
          <TableSkeleton rows={8} columns={7} />
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <MetricCard title="Total Hours" value={report.summary.totalHours} />
            <MetricCard title="Tasks Worked On" value={report.summary.tasksTouchedCount} />
            <MetricCard title="Status Updates" value={report.summary.statusUpdatesCount} />
            {/* <MetricCard title="Tasks Completed" value={report.summary.tasksCompletedCount} /> */}
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-x-auto mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours (Week)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.filter((task) => task.touched).map((task) => {
                  const isExpanded = expandedTaskId === task.taskId;
                  return (
                    <React.Fragment key={task.taskId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 align-top text-primary">
                          <div className="max-w-[24rem] whitespace-normal break-words leading-snug">{task.title}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="max-w-[12rem] truncate">{task.project || '-'}</div>
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs rounded ${
                            task.priority === 'P1' ? 'bg-red-100 text-red-800' :
                            task.priority === 'P2' ? 'bg-orange-100 text-orange-800' :
                            task.priority === 'P3' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {task.priority || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs rounded ${
                            task.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            task.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            task.status === 'In Review' ? 'bg-purple-100 text-purple-800' :
                            task.status === 'Blocked' ? 'bg-red-100 text-red-800' :
                            task.status === 'Planning' ? 'bg-yellow-100 text-yellow-800' :
                            task.status === 'On Hold' ? 'bg-orange-100 text-orange-800' :
                            task.status === 'Cancelled' ? 'bg-gray-200 text-gray-700' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {task.status || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top whitespace-nowrap font-medium">{task.hoursThisWeek.toFixed(2)}</td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">{task.lastUpdatedDate || '-'}</td>
                        <td className="px-4 py-3 align-top whitespace-nowrap">
                          <Button size="sm" variant="secondary" onClick={() => setExpandedTaskId(isExpanded ? null : task.taskId)}>
                            {isExpanded ? 'Hide Updates' : 'View Updates'}
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-4 py-4">
                            {task.updates.length === 0 ? (
                              <p className="text-sm text-gray-500">No updates this week.</p>
                            ) : (
                              <div className="space-y-3">
                                {task.updates.map((update) => (
                                  <div key={update.id} className="border rounded-lg p-3 bg-white">
                                    <p className="text-xs text-gray-500">{update.date}</p>
                                    <StatusText text={update.statusText} className="text-gray-700" />
                                    <p className="text-sm text-gray-600 mt-2">Hours: {update.timeSpentHours}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-semibold mb-4">Customer-Centric Grouping</h2>
            {groupedByCustomer.length === 0 ? (
              <p className="text-sm text-gray-500">No customer groups found.</p>
            ) : (
              groupedByCustomer.map((group) => (
                <div key={group.customerId || 'UNASSIGNED'} className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-2">Customer: {group.customerId || 'Unassigned'}</h3>
                  <div className="overflow-x-auto border rounded">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent/Child</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {group.parents.map((parent) => (
                          <React.Fragment key={parent.taskId}>
                            <tr>
                              <td className="px-4 py-2 font-medium">{parent.title}</td>
                              <td className="px-4 py-2">{parent.status || '-'}</td>
                              <td className="px-4 py-2">{parent.priority || '-'}</td>
                              <td className="px-4 py-2">{parent.hoursThisWeek.toFixed(2)}</td>
                            </tr>
                            {(parent.children || []).map((child) => (
                              <tr key={child.taskId} className="bg-gray-50">
                                <td className="px-4 py-2 pl-8">↳ {child.title}</td>
                                <td className="px-4 py-2">{child.status || '-'}</td>
                                <td className="px-4 py-2">{child.priority || '-'}</td>
                                <td className="px-4 py-2">{child.hoursThisWeek.toFixed(2)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div> */}
{/* 
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-semibold mb-4">Due Date Changes This Week</h2>
            {(report.dueDateChanges || []).length === 0 ? (
              <p className="text-sm text-gray-500">No due date changes this week.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Old</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">New</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Changed By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {report.dueDateChanges.map((change) => (
                      <tr key={change.id}>
                        <td className="px-4 py-2">{change.taskTitle}</td>
                        <td className="px-4 py-2">{change.oldDueDate || '-'}</td>
                        <td className="px-4 py-2">{change.newDueDate || '-'}</td>
                        <td className="px-4 py-2">{change.reasonTag}</td>
                        <td className="px-4 py-2">{change.changedBy?.name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div> */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <NarrativeCard title="Completed Work" items={report.narrative.completed} />
            <NarrativeCard title="Blockers" items={report.narrative.blockers} />
            <NarrativeCard title="Went Well" items={report.narrative.wentWell} />
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            {/* <h2 className="text-lg font-semibold mb-4">Manager Notes</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="4"
              className="w-full border rounded px-3 py-2"
              placeholder="Add feedback or notes for this week..."
            /> */}
            <div className="flex items-center justify-between mt-4">
              {/* <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                Reviewed
              </label> */}
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" onClick={handleDownloadCsv}>
                  Download Report (CSV)
                </Button>
                <Button type="button" variant="secondary" onClick={handleDownloadPdf}>
                  Download PDF
                </Button>
                {/* <Button type="button" onClick={handleSaveNotes} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Notes'}
                </Button> */}
                {isManager && (
                  <Button type="button" variant="secondary" onClick={handleSendDigestNow} disabled={sendingDigest}>
                    {sendingDigest ? 'Sending...' : 'Send Now'}
                  </Button>
                )}
              </div>
            </div>
            {digestMessage && (
              <p className={`mt-3 text-sm ${digestMessage.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                {digestMessage}
              </p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

const MetricCard = ({ title, value }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h3 className="text-gray-600 text-sm font-medium mb-2">{title}</h3>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
  </div>
);

const NarrativeCard = ({ title, items }) => (
  <div className="bg-white p-6 rounded-lg shadow-md">
    <h3 className="text-gray-600 text-sm font-medium mb-2">{title}</h3>
    {items && items.length > 0 ? (
      <ul className="list-disc pl-5 space-y-1 text-gray-700">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-gray-500">No highlights found.</p>
    )}
  </div>
);

export default WeeklyReportPage;
